// Room registry — the three demo interiors (Demo Build Plan).
// Each room declares its splat manifest, a *designed spawn* (Demo DoD #2:
// aligned, eye-height, best angle — these are the defaults; a manifest may
// ship a `spawn` of its own which overrides these once the splat loads),
// and interior orbit clamps sized to the room so the camera can't leave it.
export const ROOMS = {
  studio: {
    name: 'The Studio',
    manifest: '/worlds/studio.json',
    accent: '#ffb25e',
    // Entry view (user-framed): standing at the console looking dead-on at
    // the workstation + booth-window cameo. KEEP IN SYNC with the manifest's
    // spawn (the manifest wins once the splat loads).
    spawn: { pos: [0, 1.61, 1.84], look: [0, 1.6, 1.53] },
    // User-tuned in ?cam=edit: azimuth + polar fully open (free look), distance
    // effectively unclamped; bounds clamp still does wall containment.
    orbit: { min: 0.01, max: 2000, minPolar: 0, maxPolar: 3.14 },
    // Video cameo placement: couch side of the room, angled toward spawn.
    // Don't trust guesses here — tune live with ?panel=edit (arrows move,
    // PgUp/PgDn raise, [ ] rotate, -/= resize, Shift = fine) and paste the
    // console-logged line over this one.
    // Staged BEHIND the mic-booth window (the frame mattes the panel edges
    // and the frame/panel parallax feigns booth depth). billboard OFF — a
    // room behind glass must not swivel with the viewer; works with limited
    // orbit so users can't flank it. Paste tuned numbers from ?panel=edit.
    // Derived from the "on point" screenshot: card centered right-of-middle
    // behind the booth window plane (back wall ≈ z −2.4), mid-window height,
    // tall enough that the frame crops it. Fine-tune with ?panel=edit.
    videoPanel: { pos: [0.55, 1.35, -2.6], rotY: 0, height: 2.4, billboard: false },
    // Live Anam video chat — same brain/billing as the web Studio page.
    // When a session goes live, the booth-window card swaps its recorded
    // cameo for the live WebRTC stream (LiveChatDock → worldStore.liveVideoEl).
    liveChat: { avatarId: 26, name: 'Rebel Rhythm' }
  },
  'listening-room': {
    name: 'The Listening Room',
    manifest: '/worlds/listening-room.json',
    accent: '#ff5e78',
    spawn: { pos: [0, 1.6, 2.8], look: [0, 1.2, -1.4] },
    // pan: right-drag / two-finger slides the pivot so you can look anywhere,
    // including behind you (bounds still keep you off the fuzzy outer shell).
    orbit: { min: 0.5, max: 9, pan: true }
  },
  'concert-hall': {
    name: 'The Concert Hall',
    manifest: '/worlds/concert-hall.json',
    accent: '#7dd3ff',
    // firstPerson: free drag-look + WASD walk through the hall, kept inside by
    // the room bounds (and off the stage for non-admins) via CameraBounds.
    firstPerson: true,
    // Center-floor, eye height, facing the stage — mirrors where the Chisel
    // blockout places the pano camera, so splat quality is best right here.
    spawn: { pos: [0, 1.7, 7], look: [0, 2.4, -8] },
    orbit: { min: 0.5, max: 20, pan: true }
  },
  theater: {
    name: 'The Cinema',
    accent: '#c8a2ff',
    // No splat — a built black-box theater: a big flat 16:9 IMAX screen on the
    // back wall playing the /listen music videos. firstPerson: drag-look + WASD,
    // kept in the room (and out of the screen wall) by CameraBounds.
    firstPerson: true,
    // Mid-house, eye height, facing the screen (screen sits on the back wall,
    // −z). A little back from center so the big screen fills the view.
    spawn: { pos: [0, 1.7, 9], look: [0, 6, -15] },
    orbit: { min: 0.5, max: 24, pan: true },
  },
  'record-store': {
    name: 'Wax & Vinyl',
    manifest: '/worlds/record-store.json',
    accent: '#9dff7d',
    // Blend: Marble splat shop + live overlay (RecordStore.jsx).
    // firstPerson: stand still and DRAG to look (rotate in place, no orbit
    // float); WASD to pan around the shop. rooms.js spawn is authoritative
    // (RecordStore does not push the manifest spawn) so ?cam=edit "copy spawn"
    // can re-aim the entry framing.
    spawn: { pos: [-0.29, 1.6, 1.53], look: [0.62, 1.55, -0.25] }, // ?cam=edit copy-spawn
    firstPerson: true,
    // Tilt (pitch) limits for the first-person look — how far up/down the
    // visitor can look (radians; tune in ?cam=edit → "copy look config").
    look: { tiltMin: -0.12, tiltMax: 0.6 }, // ?cam=edit copy-look
    orbit: { min: 0.4, max: 12, pan: true }
  }
};

// ── The town block ────────────────────────────────────────────────────────
// The four locations are spread across a deep block (not a row): two face the
// main street, two sit back — one down an alley, the concert hall at the far
// corner. Each has a `world` footprint (where its building stands, facades on
// −z), a `street` camera pose (where you "arrive" when you pick it on the map),
// and a `map` coord (0..1, for the HUD minimap; x = left→right, y = far→near).
// Camera approaches from −z, so a smaller z is nearer the viewer.
export const STREET_OVERVIEW = { pos: [0, 8, -14], look: [1, 3, 18] };

export const TOWN_LOCATIONS = [
  {
    key: 'studio', name: 'The Studio', accent: '#ffb25e', interiorId: 'studio',
    world: [-22, 12], map: [0.16, 0.74],
    street: { pos: [-22, 3.2, 3], look: [-22, 2.9, 12] },
  },
  {
    key: 'listening-room', name: 'Listening Room', accent: '#ff5e78', interiorId: 'listening-room',
    world: [-6, 27], map: [0.42, 0.30], // down the left alley, set deep
    street: { pos: [-6, 2.8, 15], look: [-6, 2.6, 27] },
  },
  {
    key: 'record-store', name: 'Record Store', accent: '#9dff7d', interiorId: 'record-store',
    world: [20, 12], map: [0.78, 0.74],
    street: { pos: [20, 3.2, 3], look: [20, 2.9, 12] },
  },
  {
    key: 'concert-hall', name: 'Concert Hall', accent: '#7dd3ff', interiorId: 'concert-hall',
    world: [22, 31], map: [0.84, 0.22], // far corner, biggest venue
    street: { pos: [22, 4.5, 19], look: [22, 3.6, 31] },
  },
  {
    key: 'theater', name: 'The Cinema', accent: '#c8a2ff', interiorId: 'theater',
    world: [4, 40], map: [0.52, 0.08], // deepest back-center of the block
    street: { pos: [4, 3.2, 28], look: [4, 2.9, 40] },
  },
];

export const streetPoseFor = (key) => TOWN_LOCATIONS.find((l) => l.key === key)?.street || null;
